import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import CameraCapture from '@/components/shared/CameraCapture';
import { Gift, Check, Camera, Package, CaretRight } from '@phosphor-icons/react';

export default function MRGiftsPage() {
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deliverDialog, setDeliverDialog] = useState(null);

  const load = () => { api.getGifts().then(r => { setGifts(r.data); setLoading(false); }); };
  useEffect(() => { load(); }, []);

  const pending = gifts.filter(g => !g.delivered);
  const delivered = gifts.filter(g => g.delivered);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="px-4 pt-4">
        <h2 className="text-lg font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>
          Gift Distribution
        </h2>
        <p className="text-[10px] font-medium" style={{ color: '#8A94A6' }}>Track promotional gifts to doctors</p>
      </div>

      {/* Stats */}
      <div className="px-4 grid grid-cols-2 gap-3">
        <div className="p-4 rounded-xl border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <div className="flex items-center gap-2 mb-1">
            <Gift size={16} weight="duotone" style={{ color: '#FF3B30' }} />
            <span className="text-[9px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Pending</span>
          </div>
          <p className="text-2xl font-extrabold" style={{ color: '#FF3B30', fontVariantNumeric: 'tabular-nums' }}>{pending.length}</p>
        </div>
        <div className="p-4 rounded-xl border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <div className="flex items-center gap-2 mb-1">
            <Check size={16} weight="bold" style={{ color: '#34C759' }} />
            <span className="text-[9px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Delivered</span>
          </div>
          <p className="text-2xl font-extrabold" style={{ color: '#34C759', fontVariantNumeric: 'tabular-nums' }}>{delivered.length}</p>
        </div>
      </div>

      {/* Pending Gifts */}
      <div className="px-4 space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Pending Delivery</p>
        {loading ? (
          <p className="text-center py-8" style={{ color: '#8A94A6' }}>Loading...</p>
        ) : pending.length === 0 ? (
          <div className="p-8 text-center rounded-xl border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
            <Package size={40} weight="duotone" style={{ color: '#E2E4E9' }} className="mx-auto mb-2" />
            <p className="text-sm font-medium" style={{ color: '#8A94A6' }}>No pending gifts</p>
          </div>
        ) : pending.map((g, i) => (
          <div key={i} className="p-3.5 rounded-xl border flex items-center gap-3"
            style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#FF3B3010' }}>
              <Gift size={20} weight="duotone" style={{ color: '#FF3B30' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate" style={{ color: '#0A2540' }}>{g.doctor_name}</p>
              <p className="text-[10px]" style={{ color: '#8A94A6' }}>{g.gift_item} x{g.quantity}</p>
            </div>
            <Button onClick={() => setDeliverDialog(g)}
              className="h-9 px-3 text-xs font-bold rounded-lg shrink-0"
              style={{ background: '#34C759', color: 'white' }}>
              <Camera size={14} className="mr-1" /> Deliver
            </Button>
          </div>
        ))}
      </div>

      {/* Delivered Gifts */}
      {delivered.length > 0 && (
        <div className="px-4 space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Delivered</p>
          {delivered.map((g, i) => (
            <div key={i} className="p-3 rounded-xl border flex items-center gap-3"
              style={{ background: '#FFFFFF', borderColor: '#E2E4E9', opacity: 0.7 }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#34C75910' }}>
                <Check size={18} weight="bold" style={{ color: '#34C759' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate" style={{ color: '#0A2540' }}>{g.doctor_name}</p>
                <p className="text-[10px]" style={{ color: '#8A94A6' }}>
                  {g.gift_item} x{g.quantity} | {g.delivered_at?.slice(0, 10)}
                </p>
              </div>
              {g.delivery_photo && (
                <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded" style={{ background: '#34C75910', color: '#34C759' }}>
                  Photo
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Deliver Dialog with Camera */}
      <DeliverDialog gift={deliverDialog} onClose={() => setDeliverDialog(null)} onDelivered={() => {
        setDeliverDialog(null);
        load();
      }} />
    </div>
  );
}

function DeliverDialog({ gift, onClose, onDelivered }) {
  const [photo, setPhoto] = useState(null);
  const [delivering, setDelivering] = useState(false);

  if (!gift) return null;

  const deliver = async () => {
    setDelivering(true);
    try {
      let photoId = '';
      if (photo) {
        try {
          const { data } = await api.uploadPhoto(photo);
          photoId = data.photo_id;
        } catch {}
      }
      await api.deliverGift(gift._id, photoId);
      onDelivered();
    } catch {}
    setDelivering(false);
  };

  return (
    <Dialog open={!!gift} onOpenChange={onClose}>
      <DialogContent className="max-w-sm p-5">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Deliver Gift</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Gift Info */}
          <div className="p-3 rounded-xl flex items-center gap-3" style={{ background: '#F5F6F8' }}>
            <Gift size={20} weight="duotone" style={{ color: '#FF3B30' }} />
            <div>
              <p className="text-sm font-bold" style={{ color: '#0A2540' }}>{gift.doctor_name}</p>
              <p className="text-xs" style={{ color: '#8A94A6' }}>{gift.gift_item} x{gift.quantity}</p>
            </div>
          </div>

          {/* Photo Proof */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: '#8A94A6' }}>
              Proof of Delivery (Photo)
            </p>
            {photo ? (
              <div className="space-y-2">
                <img src={photo} alt="Delivery proof" className="w-full h-40 object-cover rounded-xl" />
                <button onClick={() => setPhoto(null)} className="text-xs font-semibold" style={{ color: '#007AFF' }}>
                  Retake photo
                </button>
              </div>
            ) : (
              <CameraCapture
                facing="environment"
                label="Take photo of gift handoff"
                onCapture={setPhoto}
              />
            )}
          </div>

          <Button onClick={deliver} disabled={delivering}
            className="w-full h-12 text-sm font-bold rounded-xl"
            style={{ background: '#34C759', color: 'white' }}>
            {delivering ? 'Processing...' : 'Confirm Delivery'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
